import { Link } from "react-router-dom";
import { ArrowRight, Download } from "lucide-react";
import { PullQuotePrimary, PullQuoteInline } from "@/components/PullQuote";
import { QUOTES } from "@/lib/quotes";
import { useSEO, SEO } from "@/lib/seo";

const SPEAKING_TOPICS = [
    "My life began when they told me it was over",
    "Beginning again after adversity",
    "Surviving versus feeling alive",
    "Reclaiming identity after illness",
    "Building from lived experience",
    "Patient-centred innovation",
    "Returning to music after grief",
    "Motherhood, legacy and entrepreneurship",
    "Beginning before you feel ready",
    "Your body may change, but your worth does not",
];

export default function Media() {
    useSEO(SEO.media);
    return (
        <main>
            <section className="bg-charcoal text-ivory pt-36 md:pt-44 pb-20 md:pb-28">
                <div className="container-editorial">
                    <p className="eyebrow text-gold/90">Media & Speaking</p>
                    <h1 className="display-1 mt-8 max-w-4xl">
                        For conversations that <span className="italic text-gold/95">go somewhere honest.</span>
                    </h1>
                    <p className="mt-10 body-lead text-ivory/70 max-w-2xl">
                        I speak on a small number of subjects, carefully. If a story or stage feels like the right
                        place for what I have lived through, I would be glad to hear from you.
                    </p>
                </div>
            </section>

            {/* Bio + portrait */}
            <section className="bg-ivory text-charcoal">
                <div className="container-editorial py-24 md:py-32">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                        <div className="lg:col-span-5">
                            <figure className="relative overflow-hidden aspect-[4/5] bg-ivory-soft">
                                <img
                                    src="/images/chrissa-today.jpg"
                                    alt="Chrissa Valanti in a professional portrait wearing a floral outfit."
                                    loading="lazy"
                                    className="absolute inset-0 w-full h-full object-cover"
                                    style={{ objectPosition: "center top" }}
                                />
                            </figure>
                        </div>
                        <div className="lg:col-span-7">
                            <p className="eyebrow text-oxblood">Biography</p>
                            <h2 className="display-2 mt-6">Chrissa Valanti.</h2>
                            <div className="mt-8 space-y-5 body-base text-charcoal/80">
                                <p>
                                    Chrissa Valanti is a singer, mother, founder and two-time blood cancer survivor based on
                                    her lived experience of returning to her voice, her body and her future after a long
                                    period of stepping back.
                                </p>
                                <p>
                                    She is the founder of Valanti Labs, an early-stage AI and movement-technology company,
                                    and the co-founder of ALATI, a traditional soap business she builds with her son
                                    Adonis. She continues to perform as a singer.
                                </p>
                                <p>
                                    Her work is shaped by a single question: what can be made from what life has taught us?
                                </p>
                            </div>

                            <div className="mt-10 flex flex-wrap gap-4">
                                {/* Media kit download — temporarily hidden until the real kit is supplied. Preserved here for restoration.
                                <a
                                    href="#"
                                    className="btn-secondary"
                                    data-testid="media-kit-download"
                                    onClick={(e) => e.preventDefault()}
                                >
                                    <Download size={16} strokeWidth={1.5} /> Media kit (coming soon)
                                </a>
                                */}
                                <Link to="/contact" className="btn-primary" data-testid="media-contact-cta">
                                    Make an enquiry <ArrowRight size={16} strokeWidth={1.5} />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Speaking topics */}
            <section className="bg-charcoal text-ivory">
                <div className="container-editorial py-24 md:py-32">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                        <div className="lg:col-span-5 lg:sticky lg:top-32 lg:self-start">
                            <p className="eyebrow text-gold/90">Speaking topics</p>
                            <h2 className="display-2 mt-6">
                                A small number of subjects, <span className="italic">held carefully.</span>
                            </h2>
                            <p className="mt-8 body-base text-ivory/70 max-w-md">
                                I do not present myself as a coach, clinician or motivational speaker. I speak from
                                what I have lived through, and only on subjects I can speak about honestly.
                            </p>
                        </div>
                        <div className="lg:col-span-7">
                            <ul className="divide-y divide-ivory/15">
                                {SPEAKING_TOPICS.map((t, i) => (
                                    <li key={t} className="py-5 flex items-start gap-6">
                                        <span className="eyebrow text-gold/80 pt-1 w-12 shrink-0">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <span className="font-serif text-2xl md:text-3xl leading-snug text-ivory">
                                            {t}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Inline quote */}
            <section className="bg-ivory">
                <div className="container-narrow py-24 md:py-32">
                    <PullQuoteInline>{QUOTES.story}</PullQuoteInline>
                </div>
            </section>

            {/* Pathways */}
            <section className="bg-ivory">
                <div className="container-editorial pb-24 md:pb-32">
                    <p className="eyebrow text-oxblood mb-8">Conversations I welcome</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="p-8 border border-charcoal/15">
                            <p className="font-serif text-2xl">Podcast & media</p>
                            <p className="mt-3 text-sm text-charcoal/70">Long-form, considered interviews about lived experience, building from adversity and the work of returning to your voice.</p>
                        </div>
                        <div className="p-8 border border-charcoal/15">
                            <p className="font-serif text-2xl">Stages & keynotes</p>
                            <p className="mt-3 text-sm text-charcoal/70">Selected speaking engagements where the audience and intent feel aligned with the work.</p>
                        </div>
                        <div className="p-8 border border-charcoal/15">
                            <p className="font-serif text-2xl">Music appearances</p>
                            <p className="mt-3 text-sm text-charcoal/70">Performance enquiries and collaboration requests.</p>
                        </div>
                    </div>
                    <div className="mt-12">
                        <Link to="/contact" className="btn-primary" data-testid="media-enquiry-cta">
                            Send an enquiry <ArrowRight size={16} strokeWidth={1.5} />
                        </Link>
                    </div>
                </div>
            </section>

            <PullQuotePrimary dark>{QUOTES.readerInvitation}</PullQuotePrimary>
        </main>
    );
}
